import { useCategoriesStore } from '../../store/useCategoriesStore';
import { Zap, Puzzle, Target, Trophy, Swords, Sparkles, Gamepad2 } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

interface CategoryTagProps {
  categoryId: string;
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
}

const CATEGORY_STYLES: Record<string, { icon: LucideIcon; bgColor: string; textColor: string }> = {
  'action': { icon: Zap, bgColor: 'bg-orange-100 dark:bg-orange-900/30', textColor: 'text-orange-700 dark:text-orange-400' },
  'puzzle': { icon: Puzzle, bgColor: 'bg-purple-100 dark:bg-purple-900/30', textColor: 'text-purple-700 dark:text-purple-400' },
  'strategy': { icon: Target, bgColor: 'bg-blue-100 dark:bg-blue-900/30', textColor: 'text-blue-700 dark:text-blue-400' },
  'arcade': { icon: Trophy, bgColor: 'bg-yellow-100 dark:bg-yellow-900/30', textColor: 'text-yellow-700 dark:text-yellow-400' },
  'adventure': { icon: Swords, bgColor: 'bg-red-100 dark:bg-red-900/30', textColor: 'text-red-700 dark:text-red-400' },
  'casual': { icon: Sparkles, bgColor: 'bg-pink-100 dark:bg-pink-900/30', textColor: 'text-pink-700 dark:text-pink-400' },
};

const SIZE_CLASSES = {
  sm: {
    container: 'px-2 py-1 text-xs gap-1',
    icon: 'w-3 h-3',
  },
  md: {
    container: 'px-2 py-1 text-xs gap-1',
    icon: 'w-3 h-3',
  },
  lg: {
    container: 'px-3 py-1.5 text-sm gap-1.5',
    icon: 'w-4 h-4',
  },
};

export default function CategoryTag({ categoryId, size = 'md', showIcon = true }: CategoryTagProps) {
  const { getCategoryById } = useCategoriesStore();
  const category = getCategoryById(categoryId);

  if (!category) {
    return null;
  }

  const categoryLower = categoryId.toLowerCase();
  
  // Find matching style or use default
  const matchedKey = Object.keys(CATEGORY_STYLES).find(key => categoryLower.includes(key));
  const style = matchedKey 
    ? CATEGORY_STYLES[matchedKey]
    : { icon: Gamepad2, bgColor: 'bg-gray-100 dark:bg-gray-800', textColor: 'text-gray-700 dark:text-gray-400' };

  const sizeClasses = SIZE_CLASSES[size];
  const Icon = style.icon;

  return (
    <span
      className={`inline-flex items-center rounded-md font-medium ${style.bgColor} ${style.textColor} ${sizeClasses.container}`}
    >
      {showIcon && <Icon className={sizeClasses.icon} />}
      <span>{category.displayName}</span>
    </span>
  );
}

